import { useCallback } from "react";
import type { BranchStaleness } from "../api/types";
import { useSort } from "../hooks/useSort";
import { formatDateTime, relativeAge } from "../util/format";

// How far behind develop before a branch reads as drifting / stale. Tuned to the
// cadence at which these cluster branches normally re-merge develop.
function behindLevel(behind: number): "green" | "yellow" | "red" {
    if (behind === 0) return "green";
    if (behind <= 30) return "yellow";
    return "red";
}

const COLUMNS: { col: string; label: string; sortable: boolean; title?: string }[] = [
    { col: "name", label: "Branch", sortable: true },
    { col: "behind", label: "Behind develop", sortable: true, title: "develop commits this branch is missing" },
    {
        col: "direct",
        label: "Direct commits",
        sortable: true,
        title: "non-merge commits committed straight to this branch, bypassing develop (merge commits from re-syncing develop are excluded)",
    },
    { col: "sync", label: "Last develop sync", sortable: true, title: "when develop was last merged into this branch" },
    { col: "tip", label: "Last commit", sortable: true },
    { col: "links", label: "", sortable: false },
];

export default function BranchStalenessTable({ branches, defaultBranch }: { branches: BranchStaleness[]; defaultBranch: string }) {
    const sortVal = useCallback((b: BranchStaleness, col: string): string | number => {
        switch (col) {
            case "behind":
                return b.behind;
            case "direct":
                return b.aheadDirect;
            case "sync":
                return b.syncDate ?? "";
            case "tip":
                return b.tipDate;
            default:
                return b.name;
        }
    }, []);
    // Default sort: most stale first (behind desc).
    const { sorted, toggle, arrow } = useSort(branches, sortVal, "behind", false);

    if (branches.length === 0) return <p className="muted footnote">No cluster branches found.</p>;

    return (
        <div className="table-wrap">
            <table>
                <thead>
                    <tr>
                        {COLUMNS.map((c) => (
                            <th
                                key={c.col}
                                className={c.sortable ? "sortable" : ""}
                                title={c.title}
                                onClick={c.sortable ? () => toggle(c.col) : undefined}
                            >
                                {c.label}
                                {c.sortable ? <span className="sort-arrow">{arrow(c.col)}</span> : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((b) => (
                        <BranchRow key={b.name} b={b} defaultBranch={defaultBranch} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BranchRow({ b, defaultBranch }: { b: BranchStaleness; defaultBranch: string }) {
    const level = behindLevel(b.behind);
    return (
        <tr>
            <td>
                <span className={`status-dot ${level}`} title={`${b.behind} commits behind ${defaultBranch}`} />
                <strong>{b.name}</strong>
                {b.protected ? <span className="tag-extra"> protected</span> : null}
                {b.merged ? <span className="tag-extra"> merged</span> : null}
            </td>
            <td
                className={`version ${level === "green" ? "" : "behind-cell"}`}
                title={b.truncated ? "GitLab compare truncated — at least this many" : undefined}
            >
                {b.behind === 0 ? "up to date" : `−${b.behind}`}
                {b.truncated ? "+" : ""}
            </td>
            <td className={`version ${b.aheadDirect > 0 ? "mismatch-cell" : "muted"}`}>{b.aheadDirect > 0 ? b.aheadDirect : "—"}</td>
            <td title={formatDateTime(b.syncDate)}>{b.syncDate ? relativeAge(b.syncDate) : <span className="muted">never</span>}</td>
            <td title={b.tipTitle}>{relativeAge(b.tipDate)}</td>
            <td>
                <a className="source-link" href={b.webUrl} target="_blank" rel="noopener noreferrer">
                    branch
                </a>
            </td>
        </tr>
    );
}
